/**
 * 静的レイヤー（廃ビルそのもの）はオフスクリーンに一度だけ焼く（§5.2）。
 * 毎フレーム描き直すのは猫と、プレイヤーが置いた物だけ。
 * ここは sim の状態を読むだけで、書き換えない（§4.4）。
 */
import {
  BED,
  EMPTY,
  Grid,
  PLACED,
  POST,
  SHELF,
  STRUCTURE,
  TILE,
  furnitureAt,
  salvageAt,
  tileAt,
  wearAt,
} from '../sim/grid';
import { ItemId, itemAtIndex, itemCells } from '../sim/items';
import { MATERIALS, MATERIAL_IDS } from '../sim/materials';
import { GameState, fixtureKindOf, salvageKindOf } from '../sim/state';
import { Phase, sunAt } from '../sim/sun';

export interface StaticLayer {
  canvas: HTMLCanvasElement;
  /** 焼いた時のタイル配列の指紋。構造が変わったら焼き直す */
  stamp: string;
}

function stampOf(grid: Grid): string {
  // 既存構造（STRUCTURE）だけを見る。設置物は毎フレーム描くので焼かない
  let h = 0;
  for (let i = 0; i < grid.tiles.length; i++) {
    if (grid.tiles[i] === STRUCTURE) h = (h * 31 + i) | 0;
  }
  return `${grid.cols}x${grid.rows}:${h}`;
}

export function bakeStatic(state: GameState, prev?: StaticLayer): StaticLayer {
  const grid = state.grid;
  const stamp = stampOf(grid);
  if (prev && prev.stamp === stamp) return prev;

  const canvas = prev?.canvas ?? document.createElement('canvas');
  canvas.width = grid.cols * TILE;
  canvas.height = grid.rows * TILE;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // 屋内。廃墟だが真っ暗ではない。コンクリートの灰色
  ctx.fillStyle = '#464e58';
  ctx.fillRect(TILE, TILE * 2, canvas.width - TILE * 2, canvas.height - TILE * 5);

  for (let r = 0; r < grid.rows; r++) {
    for (let c = 0; c < grid.cols; c++) {
      if (tileAt(grid, c, r) !== STRUCTURE) continue;
      drawStructure(ctx, c, r, r <= state.sun.row + 1);
    }
  }

  return { canvas, stamp };
}

function drawStructure(ctx: CanvasRenderingContext2D, c: number, r: number, lit: boolean): void {
  const x = c * TILE;
  const y = r * TILE;
  ctx.fillStyle = lit ? '#767f8c' : '#68717d';
  ctx.fillRect(x, y, TILE, TILE);
  ctx.fillStyle = 'rgba(255,255,255,.07)';
  ctx.fillRect(x, y, TILE, 2);
  ctx.fillStyle = 'rgba(0,0,0,.22)';
  ctx.fillRect(x, y + TILE - 3, TILE, 3);
  if ((c * 7 + r * 13) % 5 === 0) {
    ctx.fillStyle = 'rgba(0,0,0,.14)';
    ctx.fillRect(x + 6, y + 9, 7, 5);
  }
}

/** 時間帯ごとの空と光の色（§3.8）。締切ではなく変化として見せる */
const SKY: Record<Phase, [string, string]> = {
  dawn: ['#655a74', '#42414f'],
  day: ['#54617a', '#353b43'],
  dusk: ['#77574a', '#433a36'],
  night: ['#2b3240', '#232832'],
};
const LIGHT: Record<Phase, string> = {
  dawn: '250,200,170',
  day: '240,192,105',
  dusk: '245,155,95',
  night: '150,170,200',
};

/** 空。時間帯で色が変わる */
export function drawSky(ctx: CanvasRenderingContext2D, state: GameState, width: number): void {
  const sun = sunAt(state);
  const [top, bottom] = SKY[sun.phase];
  const g = ctx.createLinearGradient(0, 0, 0, TILE * 3);
  g.addColorStop(0, top);
  g.addColorStop(1, bottom);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, width, TILE * 3);
}

/** 日照。窓から差し込む光の帯が時間帯で動く（§3.8 / §10.6） */
export function drawSun(ctx: CanvasRenderingContext2D, state: GameState): void {
  const sun = sunAt(state);
  if (!sun.band) return;
  const { row, c0, c1 } = sun.band;
  const grid = state.grid;
  const rgb = LIGHT[sun.phase];
  const strength = 0.06 + sun.height * 0.1;

  ctx.fillStyle = `rgba(${rgb},${strength.toFixed(3)})`;
  ctx.beginPath();
  ctx.moveTo((grid.cols - 1) * TILE, (row + 1) * TILE - TILE * (1 + sun.height * 3));
  ctx.lineTo((grid.cols - 1) * TILE, (row + 1) * TILE);
  ctx.lineTo(c0 * TILE, (row + 1) * TILE);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = `rgba(${rgb},${(0.12 + sun.height * 0.16).toFixed(3)})`;
  ctx.fillRect(c0 * TILE, row * TILE + TILE * 0.55, (c1 - c0 + 1) * TILE, TILE * 0.45);
}

/** プレイヤーが置いた物。数が少ないので毎フレーム描く */
export function drawPlaced(ctx: CanvasRenderingContext2D, grid: Grid): void {
  for (let r = 0; r < grid.rows; r++) {
    for (let c = 0; c < grid.cols; c++) {
      const tile = tileAt(grid, c, r);
      if (tile !== PLACED && tile !== SHELF) continue;
      if (tile === SHELF) {
        drawShelf(ctx, grid, c, r);
        continue;
      }
      const x = c * TILE;
      const y = r * TILE;
      // 傷むほど傾いて、へこんで、色が抜ける（壊れる前に見て分かる。§2.1）
      const wear = Math.min(1, wearAt(grid, c, r) / 100);
      ctx.save();
      ctx.translate(x + TILE / 2, y + TILE / 2);
      ctx.rotate(wear * 0.09 * ((c % 2) * 2 - 1));
      ctx.translate(-TILE / 2, -TILE / 2);
      const sag = wear * 2;
      ctx.fillStyle = wear > 0.5 ? '#5d4b30' : '#6b5636';
      ctx.fillRect(3, 5 + sag, TILE - 6, TILE - 8 - sag);
      ctx.fillStyle = wear > 0.5 ? '#75603d' : '#8a7047';
      ctx.fillRect(3, 5 + sag, TILE - 6, 4);
      ctx.strokeStyle = '#4a3a24';
      ctx.lineWidth = 1;
      ctx.strokeRect(3.5, 5.5 + sag, TILE - 7, TILE - 9 - sag);
      ctx.fillStyle = 'rgba(0,0,0,.25)';
      ctx.fillRect(TILE / 2 - 6, 11 + sag, 12, 2);
      if (wear > 0.35) {
        // ほつれ・裂け目
        ctx.strokeStyle = 'rgba(20,14,8,.55)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(6, 8 + sag);
        ctx.lineTo(6 + wear * 9, 15 + sag);
        ctx.stroke();
        if (wear > 0.7) {
          ctx.beginPath();
          ctx.moveTo(TILE - 7, 10 + sag);
          ctx.lineTo(TILE - 7 - wear * 7, 19 + sag);
          ctx.stroke();
        }
      }
      ctx.restore();
    }
  }
}

/**
 * 廃ビルに置き去りにされている物（§3.4）。
 * 猫がじゃれるほど傾いて崩れていき、最後は壊れて資材になる。
 * プレイヤーには壊せない。
 */
export function drawSalvage(ctx: CanvasRenderingContext2D, state: GameState): void {
  const grid = state.grid;
  for (let r = 0; r < grid.rows; r++) {
    for (let c = 0; c < grid.cols; c++) {
      const kindIndex = salvageAt(grid, c, r);
      const kind = salvageKindOf(kindIndex);
      if (!kind) continue;
      const x = c * TILE;
      const y = r * TILE;
      const wear = Math.min(0.95, wearAt(grid, c, r) / kind.toughness);

      ctx.save();
      ctx.translate(x + TILE / 2, y + TILE);
      ctx.rotate(wear * 0.12 * ((c % 2) * 2 - 1));
      ctx.translate(-TILE / 2, -TILE);

      switch (kind.id) {
        case 'counter':
          ctx.fillStyle = '#4b4238';
          ctx.fillRect(2, 8, TILE - 4, TILE - 8);
          ctx.fillStyle = '#5e5348';
          ctx.fillRect(0, 5, TILE, 5);
          ctx.fillStyle = 'rgba(0,0,0,.25)';
          ctx.fillRect(6, 16, TILE - 12, 2);
          break;
        case 'umbrella':
          ctx.fillStyle = '#3f4650';
          ctx.fillRect(9, 12, TILE - 18, TILE - 12);
          ctx.strokeStyle = '#6b737d';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(13, 12);
          ctx.lineTo(11, 2);
          ctx.moveTo(18, 12);
          ctx.lineTo(21, 3);
          ctx.stroke();
          break;
        case 'cabinet':
          ctx.fillStyle = '#4a4d54';
          ctx.fillRect(3, 2, TILE - 6, TILE - 2);
          ctx.fillStyle = '#5b5f68';
          ctx.fillRect(3, 2, TILE - 6, 2);
          ctx.strokeStyle = 'rgba(0,0,0,.3)';
          ctx.lineWidth = 1;
          for (let i = 1; i < 4; i++) {
            ctx.beginPath();
            ctx.moveTo(4, 2 + i * 7.5);
            ctx.lineTo(TILE - 4, 2 + i * 7.5);
            ctx.stroke();
          }
          break;
        case 'desk':
          ctx.fillStyle = '#54463a';
          ctx.fillRect(1, 10, TILE - 2, 5);
          ctx.fillStyle = '#453a30';
          ctx.fillRect(4, 15, 4, TILE - 15);
          ctx.fillRect(TILE - 8, 15, 4, TILE - 15);
          break;
        case 'cupboard':
          ctx.fillStyle = '#5a4a3c';
          ctx.fillRect(2, 1, TILE - 4, TILE - 1);
          ctx.fillStyle = '#6d5a48';
          ctx.fillRect(4, 4, TILE - 8, 10);
          ctx.fillStyle = 'rgba(210,220,225,.25)';
          ctx.fillRect(5, 5, TILE - 10, 8);
          break;
        case 'bookshelf':
          ctx.fillStyle = '#4f4034';
          ctx.fillRect(2, 0, TILE - 4, TILE);
          for (let i = 0; i < 3; i++) {
            const y0 = 2 + i * 10;
            ctx.fillStyle = '#7a6a55';
            ctx.fillRect(4, y0, TILE - 8, 8);
            // 傾いて残った本
            ctx.fillStyle = ['#8a5b4a', '#5b6a8a', '#6f7a52'][i];
            ctx.fillRect(5, y0 + 1, 3, 6);
            ctx.fillRect(9, y0 + 1, 2, 6);
            ctx.save();
            ctx.translate(13, y0 + 7);
            ctx.rotate(-0.35);
            ctx.fillRect(0, -6, 2.5, 6);
            ctx.restore();
          }
          break;
        case 'sofa':
          ctx.fillStyle = '#4a3f4a';
          ctx.fillRect(0, 14, TILE, TILE - 14);
          ctx.fillStyle = '#5c4f5e';
          ctx.fillRect(0, 8, 5, 12);
          ctx.fillRect(TILE - 5, 8, 5, 12);
          ctx.fillRect(3, 10, TILE - 6, 6);
          ctx.fillStyle = 'rgba(230,220,210,.18)';
          ctx.fillRect(6, 12, TILE - 12, 2);
          break;
        case 'piano':
          ctx.fillStyle = '#2f2a2c';
          ctx.fillRect(1, 2, TILE - 2, TILE - 2);
          ctx.fillStyle = '#e8e4dc';
          ctx.fillRect(3, 14, TILE - 6, 5);
          ctx.fillStyle = '#1b1719';
          for (let i = 0; i < 5; i++) ctx.fillRect(5 + i * 4.5, 14, 1.6, 3);
          ctx.fillStyle = '#3b3436';
          ctx.fillRect(1, 2, TILE - 2, 3);
          break;
        case 'planter':
          ctx.fillStyle = '#6a4a3a';
          ctx.beginPath();
          ctx.moveTo(9, 20);
          ctx.lineTo(TILE - 9, 20);
          ctx.lineTo(TILE - 11, TILE);
          ctx.lineTo(11, TILE);
          ctx.closePath();
          ctx.fill();
          ctx.strokeStyle = '#5d5a3f';
          ctx.lineWidth = 1.4;
          ctx.beginPath();
          ctx.moveTo(16, 20);
          ctx.lineTo(13, 8);
          ctx.moveTo(16, 20);
          ctx.lineTo(20, 10);
          ctx.stroke();
          break;
        case 'vending':
          ctx.fillStyle = '#3c4a52';
          ctx.fillRect(1, 0, TILE - 2, TILE);
          ctx.fillStyle = '#22333c';
          ctx.fillRect(4, 3, TILE - 14, TILE - 8);
          ctx.fillStyle = 'rgba(150,190,200,.25)';
          ctx.fillRect(5, 4, TILE - 16, 8);
          ctx.fillStyle = '#8a7a4a';
          ctx.fillRect(TILE - 8, 6, 4, 8);
          break;
        default:
          // 木箱の山
          ctx.fillStyle = '#6b5636';
          ctx.fillRect(3, 12, TILE - 6, TILE - 12);
          ctx.fillStyle = '#7d6540';
          ctx.fillRect(6, 4, TILE - 14, 9);
          ctx.strokeStyle = '#4a3a24';
          ctx.lineWidth = 1;
          ctx.strokeRect(3.5, 12.5, TILE - 7, TILE - 13);
          break;
      }

      if (wear > 0.3) {
        ctx.strokeStyle = `rgba(20,14,8,${(wear * 0.6).toFixed(2)})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(5, 10);
        ctx.lineTo(5 + wear * 14, 22);
        ctx.stroke();
      }
      ctx.restore();
    }
  }
}

/**
 * キャットタワー。箱を積む代わりに、一度置くだけで上まで届く階段になる。
 * 廃材で組んだ、絨毯を巻いた柱と踏み板。
 */
export function drawBuilds(ctx: CanvasRenderingContext2D, state: GameState): void {
  for (const build of state.builds) {
    const item = build.item as ItemId;
    const cells = itemCells(item, build.c, build.r, build.flip);
    if (item === 'bookshelf') drawBookshelf(ctx, cells);
    else if (item === 'desk') drawDesk(ctx, cells);
    else drawTower(ctx, cells);
  }
}

function drawTower(ctx: CanvasRenderingContext2D, cells: { c: number; r: number }[]): void {
  cells.forEach((cell, i) => {
    const x = cell.c * TILE;
    const y = cell.r * TILE;
    // 踏み板
    ctx.fillStyle = '#7a6250';
    ctx.fillRect(x + 1, y + 4, TILE - 2, 7);
    ctx.fillStyle = '#8d7361';
    ctx.fillRect(x + 1, y + 4, TILE - 2, 2);
    // 巻いた紐
    ctx.strokeStyle = 'rgba(190,170,140,.5)';
    ctx.lineWidth = 1;
    for (let j = 0; j < 3; j++) {
      ctx.beginPath();
      ctx.moveTo(x + 8 + j * 6, y + 11);
      ctx.lineTo(x + 8 + j * 6, y + TILE);
      ctx.stroke();
    }
    // 支柱
    ctx.fillStyle = '#5d4c3e';
    ctx.fillRect(x + TILE / 2 - 4, y + 11, 8, TILE - 11);
    if (i === cells.length - 1) {
      ctx.fillStyle = '#8d7361';
      ctx.beginPath();
      ctx.ellipse(x + TILE / 2, y + 4, TILE / 2 - 1, 3.5, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  });
}

/** 本棚。縦2マス。てっぺんに猫が乗るので、天板をはっきり描く */
function drawBookshelf(ctx: CanvasRenderingContext2D, cells: { c: number; r: number }[]): void {
  const top = cells.reduce((a, b) => (b.r < a.r ? b : a));
  for (const cell of cells) {
    const x = cell.c * TILE;
    const y = cell.r * TILE;
    ctx.fillStyle = '#6a533f';
    ctx.fillRect(x + 2, y, TILE - 4, TILE);
    ctx.fillStyle = '#3a2d23';
    ctx.fillRect(x + 4, y + 4, TILE - 8, TILE - 8);
    // 背表紙
    const books = ['#8a5648', '#5f7355', '#7a6a45', '#4f5f72'];
    for (let i = 0; i < 5; i++) {
      ctx.fillStyle = books[(cell.c + cell.r + i) % books.length];
      ctx.fillRect(x + 5 + i * 4, y + 6 + ((i * 3) % 4), 3, TILE - 12 - ((i * 3) % 4));
    }
  }
  const x = top.c * TILE;
  const y = top.r * TILE;
  ctx.fillStyle = '#8d7361';
  ctx.fillRect(x, y - 2, TILE, 4);
}

/** 机。横2マス。天板の上が猫の指定席になる */
function drawDesk(ctx: CanvasRenderingContext2D, cells: { c: number; r: number }[]): void {
  const left = Math.min(...cells.map((c) => c.c)) * TILE;
  const y = cells[0].r * TILE;
  const w = cells.length * TILE;
  ctx.fillStyle = '#8d7361';
  ctx.fillRect(left, y + 2, w, 5);
  ctx.fillStyle = '#5d4c3e';
  ctx.fillRect(left + 3, y + 7, 5, TILE - 7);
  ctx.fillRect(left + w - 8, y + 7, 5, TILE - 7);
  ctx.fillStyle = '#6a533f';
  ctx.fillRect(left + 8, y + 8, w - 16, 6);
}

/**
 * 廃墟に残っている飾り（§3.4）。
 * 足場ではないので、絵としても**床から浮いている**ことがはっきり分かるように描く。
 * 高い所の物ほど、届く猫が限られる。
 */
export function drawFixtures(ctx: CanvasRenderingContext2D, state: GameState): void {
  for (const f of state.fixtures) {
    const kind = fixtureKindOf(f.kind);
    const x = f.c * TILE;
    const y = f.r * TILE;
    const hurt = kind ? Math.min(1, f.wear / kind.toughness) : 0;
    ctx.save();
    // 叩かれるほど傾く（§2.1 数値ではなく絵で）
    ctx.translate(x + TILE / 2, y + TILE / 2);
    ctx.rotate(hurt * 0.28);
    ctx.translate(-TILE / 2, -TILE / 2);
    drawFixtureArt(ctx, f.kind, kind?.mount ?? 'wall');
    ctx.restore();
  }
}

function drawFixtureArt(
  ctx: CanvasRenderingContext2D,
  kind: string,
  mount: 'wall' | 'ceiling',
): void {
  const m = TILE / 2;
  if (mount === 'ceiling') {
    ctx.strokeStyle = 'rgba(150,150,150,.45)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(m, 0);
    ctx.lineTo(m, 8);
    ctx.stroke();
  }
  switch (kind) {
    case 'clock':
      ctx.fillStyle = '#4a4038';
      ctx.beginPath();
      ctx.arc(m, m, 9, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#cfc4ae';
      ctx.beginPath();
      ctx.arc(m, m, 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#3a332c';
      ctx.beginPath();
      ctx.moveTo(m, m);
      ctx.lineTo(m + 4, m - 2);
      ctx.moveTo(m, m);
      ctx.lineTo(m, m - 5);
      ctx.stroke();
      break;
    case 'frame':
      ctx.fillStyle = '#6a533f';
      ctx.fillRect(m - 9, m - 7, 18, 14);
      ctx.fillStyle = '#3d4a52';
      ctx.fillRect(m - 7, m - 5, 14, 10);
      ctx.fillStyle = '#55636b';
      ctx.fillRect(m - 7, m + 1, 14, 4);
      break;
    case 'tube':
      ctx.fillStyle = '#8f9aa2';
      ctx.fillRect(m - 12, 8, 24, 5);
      ctx.fillStyle = 'rgba(220,235,240,.35)';
      ctx.fillRect(m - 11, 9, 22, 2);
      break;
    case 'extinguisher':
      ctx.fillStyle = '#8b3b32';
      ctx.fillRect(m - 5, m - 6, 10, 15);
      ctx.fillStyle = '#5b5b5b';
      ctx.fillRect(m - 3, m - 10, 6, 5);
      break;
    case 'curtain':
      ctx.fillStyle = '#6c5563';
      for (let i = 0; i < 4; i++) {
        ctx.fillRect(m - 10 + i * 5, 2, 4, TILE - 6);
      }
      ctx.fillStyle = '#8d9aa8';
      ctx.fillRect(m - 12, 1, 24, 2);
      break;
    case 'panel':
      ctx.fillStyle = '#5d6570';
      ctx.fillRect(m - 9, m - 9, 18, 18);
      ctx.fillStyle = '#3c434b';
      ctx.fillRect(m - 7, m - 7, 14, 14);
      ctx.fillStyle = '#c8b45a';
      ctx.fillRect(m - 5, m - 5, 4, 3);
      break;
    case 'birdcage':
      ctx.strokeStyle = '#8d9aa8';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(m, m + 2, 8, Math.PI, 0);
      ctx.stroke();
      for (let i = -6; i <= 6; i += 3) {
        ctx.beginPath();
        ctx.moveTo(m + i, m - 4);
        ctx.lineTo(m + i, m + 9);
        ctx.stroke();
      }
      ctx.beginPath();
      ctx.moveTo(m - 8, m + 9);
      ctx.lineTo(m + 8, m + 9);
      ctx.stroke();
      break;
    case 'bottle':
      ctx.fillStyle = 'rgba(111,182,189,.55)';
      ctx.fillRect(m - 5, m - 3, 10, 12);
      ctx.fillRect(m - 2, m - 8, 4, 5);
      ctx.fillStyle = 'rgba(200,230,235,.5)';
      ctx.fillRect(m - 4, m - 2, 2, 9);
      break;
    case 'pipe':
      ctx.fillStyle = '#79838d';
      ctx.fillRect(0, m - 4, TILE, 8);
      ctx.fillStyle = '#5b6672';
      ctx.fillRect(m - 3, m - 6, 6, 12);
      break;
    case 'banner':
      ctx.fillStyle = '#7d5566';
      ctx.beginPath();
      ctx.moveTo(m - 9, 4);
      ctx.lineTo(m + 9, 4);
      ctx.lineTo(m, TILE - 4);
      ctx.closePath();
      ctx.fill();
      break;
    case 'mirror':
      ctx.fillStyle = '#7a6250';
      ctx.beginPath();
      ctx.ellipse(m, m, 8, 11, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(150,190,195,.5)';
      ctx.beginPath();
      ctx.ellipse(m, m, 6, 9, 0, 0, Math.PI * 2);
      ctx.fill();
      break;
    default: // board
      ctx.fillStyle = '#7a6250';
      ctx.fillRect(m - 12, m - 2, 24, 4);
      ctx.fillStyle = '#5d4c3e';
      ctx.fillRect(m - 10, m + 2, 3, 4);
      ctx.fillRect(m + 7, m + 2, 3, 4);
      break;
  }
}

/**
 * プレイヤーが壁に飾った物（§10.19）。
 * 何の役にも立たない。荒れた廃墟が、少しずつ誰かの部屋に見えてくるためだけにある。
 */
export function drawDecor(ctx: CanvasRenderingContext2D, grid: Grid): void {
  for (let r = 0; r < grid.rows; r++) {
    for (let c = 0; c < grid.cols; c++) {
      const def = itemAtIndex(grid.decor[r * grid.cols + c]);
      if (!def) continue;
      ctx.save();
      ctx.translate(c * TILE, r * TILE);
      drawDecorArt(ctx, def.id);
      ctx.restore();
    }
  }
}

function drawDecorArt(ctx: CanvasRenderingContext2D, id: ItemId): void {
  const m = TILE / 2;
  if (id === 'frame') {
    ctx.fillStyle = '#8d7361';
    ctx.fillRect(m - 10, m - 8, 20, 16);
    ctx.fillStyle = '#2f3a42';
    ctx.fillRect(m - 8, m - 6, 16, 12);
    // 中身は描かない。何が飾ってあるかは見る人の勝手（§1）
    ctx.fillStyle = '#4d6070';
    ctx.beginPath();
    ctx.moveTo(m - 8, m + 6);
    ctx.lineTo(m - 2, m - 2);
    ctx.lineTo(m + 3, m + 3);
    ctx.lineTo(m + 8, m - 1);
    ctx.lineTo(m + 8, m + 6);
    ctx.closePath();
    ctx.fill();
    return;
  }
  if (id === 'clock') {
    ctx.fillStyle = '#5b6672';
    ctx.beginPath();
    ctx.arc(m, m, 9, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#e8e0cc';
    ctx.beginPath();
    ctx.arc(m, m, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#3a332c';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(m, m);
    ctx.lineTo(m - 4, m - 3);
    ctx.moveTo(m, m);
    ctx.lineTo(m + 1, m - 6);
    ctx.stroke();
    return;
  }
  if (id === 'lamp') {
    ctx.strokeStyle = 'rgba(160,160,160,.5)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(m, 0);
    ctx.lineTo(m, 9);
    ctx.stroke();
    ctx.fillStyle = '#6fb6bd';
    ctx.beginPath();
    ctx.moveTo(m - 9, 18);
    ctx.lineTo(m + 9, 18);
    ctx.lineTo(m + 5, 9);
    ctx.lineTo(m - 5, 9);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = 'rgba(255,236,180,.28)';
    ctx.beginPath();
    ctx.moveTo(m - 9, 18);
    ctx.lineTo(m + 9, 18);
    ctx.lineTo(m + 13, TILE);
    ctx.lineTo(m - 13, TILE);
    ctx.closePath();
    ctx.fill();
    return;
  }
  // pennant
  ctx.strokeStyle = 'rgba(190,170,140,.5)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, 8);
  ctx.quadraticCurveTo(m, 13, TILE, 8);
  ctx.stroke();
  const colors = ['#b3708f', '#6fb6bd', '#b98a4e'];
  for (let i = 0; i < 3; i++) {
    ctx.fillStyle = colors[i];
    const px = 5 + i * 11;
    ctx.beginPath();
    ctx.moveTo(px, 10);
    ctx.lineTo(px + 8, 10);
    ctx.lineTo(px + 4, 19);
    ctx.closePath();
    ctx.fill();
  }
}

/**
 * 床に落ちている資材（§10.8）。
 *
 * **猫は拾わない。** プレイヤーが押すまでそこにある。
 * 廃墟の中の物とまぎれないよう、はっきりキラキラさせる。
 * これは「拾えます」というUIではなく、資材そのものの見た目（§2.1）。
 */
export function drawDrops(ctx: CanvasRenderingContext2D, state: GameState, t: number): void {
  for (const pile of state.drops) {
    const ids = MATERIAL_IDS.filter((id) => (pile.mats[id] ?? 0) > 0);
    if (!ids.length) continue;
    const x = pile.c * TILE;
    const y = pile.r * TILE;
    const seed = pile.c * 7 + pile.r * 13;
    const pulse = 0.5 + 0.5 * Math.sin(t * 2.4 + seed);
    const cx = x + TILE / 2;
    const cy = y + TILE - 9;

    // 足元のにじみ。暗い床でも位置が分かる
    const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, 15 + pulse * 4);
    glow.addColorStop(0, `rgba(255,240,190,${(0.34 + pulse * 0.2).toFixed(3)})`);
    glow.addColorStop(0.55, 'rgba(255,225,150,.12)');
    glow.addColorStop(1, 'rgba(255,225,150,0)');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(cx, cy, 16 + pulse * 4, 0, Math.PI * 2);
    ctx.fill();

    // 資材そのもの。種類ごとの色で、何が落ちているか色で読める
    ids.forEach((id, i) => {
      const kind = MATERIALS.find((m) => m.id === id)!;
      const px = cx + (i - (ids.length - 1) / 2) * 9;
      const py = cy + Math.sin(t * 2 + i + seed) * 1.1;
      ctx.fillStyle = kind.dark;
      ctx.fillRect(px - 4.5, py - 3.5, 9, 8);
      ctx.fillStyle = kind.color;
      ctx.fillRect(px - 4.5, py - 3.5, 9, 2.5);
      ctx.fillRect(px - 4.5, py - 3.5, 2.5, 8);
      ctx.fillStyle = 'rgba(255,255,255,.35)';
      ctx.fillRect(px - 3.5, py - 2.5, 2, 1);
    });

    // きらめき。位相をずらした2つの十字が、順ぐりに光る
    for (let k = 0; k < 2; k++) {
      const ph = (t * 1.5 + seed * 0.31 + k * 0.5) % 1;
      const a = Math.sin(ph * Math.PI);
      if (a <= 0.02) continue;
      const sx = cx + Math.cos(seed + k * 2.4) * 9;
      const sy = cy - 6 + Math.sin(seed * 1.7 + k * 2.4) * 6;
      drawSparkle(ctx, sx, sy, 3 + a * 4, a);
    }
  }
}

/** 4つ角の光。細い十字だけで、丸は描かない（ドット絵の中で浮かないように） */
function drawSparkle(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  alpha: number,
): void {
  ctx.save();
  ctx.globalAlpha = Math.min(1, alpha);
  ctx.fillStyle = '#fff6d8';
  ctx.fillRect(x - size, y - 0.7, size * 2, 1.4);
  ctx.fillRect(x - 0.7, y - size, 1.4, size * 2);
  ctx.fillStyle = 'rgba(255,255,255,.9)';
  ctx.fillRect(x - 1, y - 1, 2, 2);
  ctx.restore();
}

/**
 * 見つかった思い出（§1 裏テーマ）。
 * 廃墟になる前の物が、猫が長く過ごした場所に見つかる。
 * 説明はしない。物がそこに在るだけ。
 */
export function drawMemories(ctx: CanvasRenderingContext2D, state: GameState): void {
  for (const m of state.memories) {
    if (!m.found) continue;
    const x = m.c * TILE + TILE / 2;
    const y = (m.r + 1) * TILE;
    ctx.save();
    switch (m.kind) {
      case 'bowl':
        ctx.fillStyle = '#9a8f7a';
        ctx.beginPath();
        ctx.ellipse(x, y - 3, 7, 3, 0, Math.PI, 0);
        ctx.fill();
        ctx.fillStyle = 'rgba(0,0,0,.35)';
        ctx.fillRect(x + 3, y - 6, 3, 3);
        break;
      case 'cloth':
        ctx.fillStyle = '#6b5f74';
        ctx.fillRect(x - 8, y - 5, 16, 5);
        ctx.fillStyle = '#7d7188';
        ctx.fillRect(x - 8, y - 7, 16, 3);
        break;
      case 'toy':
        ctx.strokeStyle = '#8a7a5a';
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(x - 6, y - 1);
        ctx.lineTo(x + 4, y - 9);
        ctx.stroke();
        ctx.fillStyle = '#c06a72';
        ctx.beginPath();
        ctx.arc(x + 5, y - 10, 2.4, 0, Math.PI * 2);
        ctx.fill();
        break;
      case 'notice':
        ctx.fillStyle = '#d8d2c4';
        ctx.fillRect(x - 6, y - 14, 12, 13);
        ctx.strokeStyle = 'rgba(60,55,50,.45)';
        ctx.lineWidth = 1;
        for (let i = 0; i < 3; i++) {
          ctx.beginPath();
          ctx.moveTo(x - 4, y - 11 + i * 3);
          ctx.lineTo(x + 4, y - 11 + i * 3);
          ctx.stroke();
        }
        break;
      case 'photo':
        ctx.fillStyle = '#e6e0d2';
        ctx.fillRect(x - 6, y - 9, 12, 9);
        ctx.fillStyle = '#7c8a76';
        ctx.fillRect(x - 5, y - 8, 10, 6);
        ctx.fillStyle = '#d8cbb6';
        ctx.beginPath();
        ctx.arc(x, y - 4, 1.8, 0, Math.PI * 2);
        ctx.fill();
        break;
      case 'collar':
        for (let i = 0; i < 3; i++) {
          ctx.strokeStyle = ['#b05a5a', '#5a7ab0', '#b0a05a'][i];
          ctx.lineWidth = 1.6;
          ctx.beginPath();
          ctx.arc(x - 6 + i * 6, y - 3, 2.6, 0, Math.PI * 2);
          ctx.stroke();
        }
        break;
      case 'hatch':
        ctx.strokeStyle = 'rgba(200,210,220,.5)';
        ctx.lineWidth = 1.2;
        ctx.strokeRect(x - 6, y - 12, 12, 11);
        ctx.beginPath();
        ctx.moveTo(x - 6, y - 12);
        ctx.lineTo(x + 2, y - 16);
        ctx.stroke();
        break;
      default:
        // 爪あと
        ctx.strokeStyle = 'rgba(160,140,120,.6)';
        ctx.lineWidth = 1;
        for (let i = 0; i < 4; i++) {
          ctx.beginPath();
          ctx.moveTo(x - 6 + i * 4, y - 2);
          ctx.lineTo(x - 5 + i * 4, y - 12);
          ctx.stroke();
        }
        break;
    }
    // 見つけた物には、ごく淡い光を添える（探し物ではないので目立たせすぎない）
    ctx.fillStyle = 'rgba(240,220,180,.07)';
    ctx.beginPath();
    ctx.arc(x, y - 7, 11, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

/** 棚（§3.7 オフィスを開通させると作れる）。箱より薄くて丈夫 */
function drawShelf(ctx: CanvasRenderingContext2D, grid: Grid, c: number, r: number): void {
  const x = c * TILE;
  const y = r * TILE;
  const wear = Math.min(1, wearAt(grid, c, r) / 100);
  ctx.fillStyle = '#6a6f78';
  ctx.fillRect(x + 1, y + TILE - 12 + wear * 2, TILE - 2, 6);
  ctx.fillStyle = '#828892';
  ctx.fillRect(x + 1, y + TILE - 12 + wear * 2, TILE - 2, 2);
  ctx.fillStyle = '#4c525a';
  ctx.fillRect(x + 3, y + TILE - 6 + wear * 2, 3, 6);
  ctx.fillRect(x + TILE - 6, y + TILE - 6 + wear * 2, 3, 6);
  ctx.fillStyle = 'rgba(0,0,0,.22)';
  ctx.fillRect(x + 1, y + TILE - 6 + wear * 2, TILE - 2, 2);
}

/** 爪とぎ（§3.7 食堂を開通させると作れる）。まわりの設置物が傷みにくくなる */
function drawPost(ctx: CanvasRenderingContext2D, c: number, r: number): void {
  const x = c * TILE + TILE / 2;
  const y = (r + 1) * TILE;
  ctx.fillStyle = '#5a4a35';
  ctx.fillRect(x - 7, y - 4, 14, 4);
  ctx.fillStyle = '#b39a6d';
  ctx.fillRect(x - 4, y - 22, 8, 18);
  ctx.strokeStyle = 'rgba(90,74,53,.55)';
  ctx.lineWidth = 1;
  for (let i = 0; i < 5; i++) {
    ctx.beginPath();
    ctx.moveTo(x - 4, y - 20 + i * 4);
    ctx.lineTo(x + 4, y - 18 + i * 4);
    ctx.stroke();
  }
}

/** 寝床。猫が乗るだけで通行の邪魔はしない（§3.4 快適系） */
export function drawBeds(ctx: CanvasRenderingContext2D, grid: Grid): void {
  for (let r = 0; r < grid.rows; r++) {
    for (let c = 0; c < grid.cols; c++) {
      const furn = furnitureAt(grid, c, r);
      if (furn === POST) {
        drawPost(ctx, c, r);
        continue;
      }
      if (furn !== BED) continue;
      const x = c * TILE;
      const y = (r + 1) * TILE;
      const wear = Math.min(1, wearAt(grid, c, r) / 100);
      ctx.fillStyle = wear > 0.5 ? '#403747' : '#4a3f52';
      ctx.beginPath();
      ctx.ellipse(x + TILE / 2, y - 4, 12, 5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = wear > 0.5 ? '#4f4459' : '#5c4f66';
      ctx.beginPath();
      ctx.ellipse(x + TILE / 2, y - 5.5 + wear * 1.5, 12 - wear * 2, 4.5 - wear, 0, Math.PI, 0);
      ctx.fill();
      if (wear > 0.4) {
        // 綿がはみ出している
        ctx.fillStyle = 'rgba(220,214,205,.5)';
        ctx.fillRect(x + 6, y - 7, 3, 2);
        ctx.fillRect(x + TILE - 10, y - 6, 2, 2);
      }
      ctx.fillStyle = '#2b2533';
      ctx.beginPath();
      ctx.ellipse(x + TILE / 2, y - 5, 8, 2.6, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

/**
 * 荒れ（爪とぎ跡・抜け毛）。統計画面の代わりに、部屋の様子で混雑を読む（§3.5）。
 * 猫が居着いた場所ほど濃くなる。
 */
export function drawTraces(ctx: CanvasRenderingContext2D, state: GameState): void {
  const grid = state.grid;
  for (let k = 0; k < state.traces.length; k++) {
    const n = state.traces[k];
    if (n <= 0) continue;
    const c = k % grid.cols;
    const r = (k / grid.cols) | 0;
    const x = c * TILE;
    const y = (r + 1) * TILE;
    const density = Math.min(6, n);
    for (let i = 0; i < density; i++) {
      // 位置は場所と本数から決める。毎フレーム動かさない
      const h = (c * 31 + r * 17 + i * 7) % 23;
      const px = x + 4 + ((h * 5) % (TILE - 8));
      const py = y - 2 - (h % 3);
      ctx.fillStyle = i % 2 === 0 ? 'rgba(150,140,125,.30)' : 'rgba(110,100,90,.30)';
      if (i % 3 === 0) {
        ctx.fillRect(px, py - 3, 1, 3); // 爪とぎ跡
      } else {
        ctx.fillRect(px, py, 2, 1); // 抜け毛
      }
    }
  }
}

/** 割れた窓。外から猫が入ってくる場所（§3.6） */
export function drawEntrance(ctx: CanvasRenderingContext2D, state: GameState): void {
  const x = state.entrance.c * TILE;
  const y = state.entrance.r * TILE;
  // 外の光
  const g = ctx.createLinearGradient(x + TILE, y, x, y + TILE);
  g.addColorStop(0, 'rgba(150,170,190,.16)');
  g.addColorStop(1, 'rgba(150,170,190,0)');
  ctx.fillStyle = g;
  ctx.fillRect(x - TILE, y, TILE * 2, TILE);
  // 割れたガラスの残り
  ctx.fillStyle = 'rgba(180,205,220,.35)';
  ctx.beginPath();
  ctx.moveTo(x + TILE, y);
  ctx.lineTo(x + TILE - 9, y);
  ctx.lineTo(x + TILE, y + 11);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(x, y + TILE);
  ctx.lineTo(x + 7, y + TILE);
  ctx.lineTo(x, y + TILE - 8);
  ctx.closePath();
  ctx.fill();
}

/** 餌場（下層にしかない。§2.7） */
export function drawBowl(ctx: CanvasRenderingContext2D, state: GameState): void {
  const x = state.bowl.c * TILE + TILE / 2;
  const y = (state.bowl.r + 1) * TILE;
  ctx.fillStyle = '#7a8794';
  ctx.beginPath();
  ctx.ellipse(x, y - 3, 9, 4, 0, Math.PI, 0);
  ctx.fill();
  ctx.fillStyle = '#b8a074';
  ctx.beginPath();
  ctx.ellipse(x, y - 6, 6, 2.2, 0, 0, Math.PI * 2);
  ctx.fill();
}

/** 水場（餌場と同じく下層にしかない。§2.7） */
export function drawWater(ctx: CanvasRenderingContext2D, state: GameState): void {
  const x = state.water.c * TILE + TILE / 2;
  const y = (state.water.r + 1) * TILE;
  // 割れた配管から落ちた水が溜まっている、という体
  ctx.fillStyle = 'rgba(120,170,190,.35)';
  ctx.beginPath();
  ctx.ellipse(x, y - 2, 11, 3.2, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgba(180,220,235,.5)';
  ctx.beginPath();
  ctx.ellipse(x - 3, y - 2.5, 4, 1.2, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = 'rgba(120,170,190,.25)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x + 2, y - TILE * 0.9);
  ctx.lineTo(x + 1, y - 4);
  ctx.stroke();
}

export function drawFloorLabels(
  ctx: CanvasRenderingContext2D,
  floors: readonly { name: string; standRow: number }[],
): void {
  ctx.fillStyle = 'rgba(109,121,134,.7)';
  ctx.font = '9px ui-monospace,monospace';
  for (const f of floors) {
    ctx.fillText(f.name, 6, f.standRow * TILE + 8);
  }
}

export function isPlaceable(grid: Grid, c: number, r: number): boolean {
  return tileAt(grid, c, r) === EMPTY;
}
